import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { api, ApiError } from '@/lib/api';

const RESERVED = new Set([
  'admin', 'administrator', 'fishymeter', 'anonymous', 'support', 'help',
  'about', 'api', 'auth', 'login', 'logout', 'signup', 'signin', 'me', 'user',
  'users', 'catch', 'feed', 'welcome', 'settings', 'og', 'static', 'www',
  'root', 'system', 'null', 'undefined',
]);

const usernameSchema = z
  .string()
  .trim()
  .min(3, '3 characters minimum')
  .max(20, '20 characters maximum')
  .regex(/^[a-zA-Z0-9_]+$/, 'Letters, digits, and underscore only')
  .refine((s) => !RESERVED.has(s.toLowerCase()), 'That username is reserved');

export function WelcomePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [username, setUsername] = useState('');
  const [touched, setTouched] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [agreed, setAgreed] = useState(false);

  const validation = usernameSchema.safeParse(username);
  const localError = touched && username.length > 0 && !validation.success
    ? validation.error.issues[0]?.message
    : null;

  const claim = useMutation({
    mutationFn: (name: string) => api.setUsername(name, new Date().toISOString()),
    onSuccess: (profile) => {
      queryClient.setQueryData(['me'], profile);
      navigate('/', { replace: true });
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        if (err.code === 'USERNAME_TAKEN') {
          setServerError('That username is already taken. Try another.');
        } else if (err.code === 'PROFILE_EXISTS') {
          // Race: profile got created elsewhere. Just go home.
          navigate('/', { replace: true });
        } else {
          setServerError(err.message);
        }
      } else {
        setServerError('Something went wrong. Try again.');
      }
    },
  });

  return (
    <div className="mx-auto max-w-md px-4 py-16 sm:px-6">
      <h1 className="text-3xl font-bold tracking-tight">Welcome aboard 🎣</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Pick a username. It'll show up on every analysis you publish.
      </p>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          setServerError(null);
          if (!validation.success) {
            setTouched(true);
            return;
          }
          if (!agreed) return;
          claim.mutate(validation.data);
        }}
        className="mt-8"
      >
        <label htmlFor="username" className="block text-sm font-medium">
          Username
        </label>
        <div className="mt-2 flex items-center rounded-md border border-border bg-card px-3 py-2 shadow-sm focus-within:ring-2 focus-within:ring-primary">
          <span className="text-sm text-muted-foreground">@</span>
          <input
            id="username"
            type="text"
            value={username}
            onChange={(e) => {
              setUsername(e.target.value);
              setServerError(null);
            }}
            onBlur={() => setTouched(true)}
            autoFocus
            placeholder="kingfisher90"
            className="ml-1 block w-full bg-transparent text-sm focus:outline-none"
            disabled={claim.isPending}
          />
        </div>
        {(localError || serverError) && (
          <p className="mt-2 text-xs text-destructive">{serverError ?? localError}</p>
        )}
        <p className="mt-2 text-xs text-muted-foreground">
          3–20 characters. Letters, digits, underscore.
        </p>

        <label className="mt-6 flex cursor-pointer items-start gap-2 text-sm text-foreground">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-border text-primary focus:ring-primary"
            disabled={claim.isPending}
          />
          <span>
            I agree to the{' '}
            <Link to="/terms" target="_blank" className="text-primary underline hover:text-primary/80">
              Terms
            </Link>{' '}
            and{' '}
            <Link to="/privacy" target="_blank" className="text-primary underline hover:text-primary/80">
              Privacy Policy
            </Link>
            .
          </span>
        </label>

        <button
          type="submit"
          disabled={claim.isPending || !validation.success || !agreed}
          className="mt-6 w-full rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground shadow-sm hover:bg-primary/90 disabled:opacity-60"
        >
          {claim.isPending ? 'Claiming…' : 'Claim username'}
        </button>
      </form>
    </div>
  );
}
